import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import downloadIcon from "assets/svg/download.svg";
import HoverAuthorizeTooltip from "components/authorize/AuthorizeTooltip";
import DateTimeFilter from "components/common/DateTimeFilter";
import { HistoryTable } from "components/events/EventHistory/historyTable";
import moment, { Moment } from "moment-timezone";
import { useCallback, useEffect, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { isApiError } from "service/baseService";
import { eventJournalService } from "service/eventJournalService";
import { EventJournal, Event } from "types/Event";
import { showErrorAlert } from "utils/alert";
import { dateTimeFormatter } from "utils/dateTimeFormatter";
import { HttpStatus } from "constant";
import { useOrganisation } from "hooks/organization";
import { isHttpSuccess } from "utils/functions";
import { extractDownloadFilename } from "utils/gatewayFunctions";

interface EventHistoryProps {
    event: Event;
}

const MAX_LOAD_DAYS = 10;

const toUtcIsoString = (date: Date) => moment(date).utc().toISOString();

export const EventHistory = ({ event }: EventHistoryProps) => {
    // Track the oldest fetched start date for load more
    const [oldestFetchedStart, setOldestFetchedStart] = useState<Date>(
        moment().subtract(MAX_LOAD_DAYS, "days").add(10, "seconds").toDate()
    );
    const [allJournals, setAllJournals] = useState<EventJournal[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const { defaultTimeZone } = useOrganisation();
    const [isFilterData, setIsFilterData] = useState(false);
    const [filterStart, setFilterStart] = useState<Date | null>(null);
    const [filterEnd, setFilterEnd] = useState<Date | null>(null);
    const [dateFilterMessage, setDateFilterMessage] = useState("");

    const fetchJournals = useCallback(
        async (start: Date, end: Date, append = false) => {
            try {
                setIsLoading(true);
                const res = await eventJournalService.getEventJournals({
                    event_uuid: event.uuid,
                    start: toUtcIsoString(start),
                    end: toUtcIsoString(end),
                });
                setIsLoading(false);
                if (isApiError(res)) {
                    showErrorAlert({
                        message: res.error.message ?? "Failed to get journals",
                    });
                    return;
                }

                if (append) {
                    setAllJournals((prev) => [...prev, ...res.data]);
                } else {
                    setAllJournals(res.data);
                }
            } catch (error) {
                setIsLoading(false);
                showErrorAlert({
                    message:
                        (error as Error).message ?? "Failed to get journals",
                });
            }
        },
        [event.uuid]
    );

    useEffect(() => {
        const initialStart = moment().subtract(MAX_LOAD_DAYS, "days").add(10, "seconds").toDate();
        const initialEnd = new Date();
        setOldestFetchedStart(initialStart);
        fetchJournals(initialStart, initialEnd);
    }, [event.uuid, fetchJournals]);

    const handleFilter = useCallback((start: Moment, end: Moment) => {
        const startDate = start.toDate();
        const endDate = end.toDate();
        setFilterStart(startDate);
        setFilterEnd(endDate);
        setIsFilterData(true);
        fetchJournals(startDate, endDate);
    }, [fetchJournals]);

    const handleClearFilter = useCallback(() => {
        setIsFilterData(false);
        setFilterStart(null);
        setFilterEnd(null);
        const initialStart = moment().subtract(MAX_LOAD_DAYS, "days").add(10, "seconds").toDate();
        const initialEnd = new Date();
        setOldestFetchedStart(initialStart);
        fetchJournals(initialStart, initialEnd);
    }, [fetchJournals]);

    const handleDownloadData = useCallback(async () => {
        const res = await eventJournalService.downloadEventJournals({
            event_uuid: event.uuid,
            time_zone: defaultTimeZone,
        });

        if (isHttpSuccess(res.status)) {
            const headers = "headers" in res ? res.headers : undefined;
            const fileName = extractDownloadFilename(headers);
            const link = document.createElement("a");
            const url = globalThis.URL.createObjectURL(res.data);
            link.href = url;
            link.download = fileName;
            link.click();
            globalThis.URL.revokeObjectURL(url);
        } else if (res.status === HttpStatus.TOO_MANY_REQUESTS) {
            showErrorAlert({
                title: "Error",
                message: "Server is busy. Please try again later.",
            });
        } else {
            showErrorAlert({
                title: "Error",
                message: "Unable to download data. Please try again.",
            });
        }
    }, [event.uuid, defaultTimeZone]);

    // Load more: fetch the next 10-day chunk going backwards
    const handleLoadMore = useCallback(() => {
        const newEnd = moment(oldestFetchedStart).toDate();
        const newStart = moment(oldestFetchedStart).subtract(MAX_LOAD_DAYS, "days").toDate();
        setOldestFetchedStart(newStart);
        fetchJournals(newStart, newEnd, true);
    }, [oldestFetchedStart, fetchJournals]);

    return (
        <>
            <div className="mb-3">
                <Row className="align-items-end">
                    <Col>
                        <DateTimeFilter
                            onFilter={handleFilter}
                            onValidationMessageChange={setDateFilterMessage}
                        />
                    </Col>
                    <Col xs="auto">
                        <HoverAuthorizeTooltip permission="event:update">
                            <Button
                                variant="primary"
                                className="date-time-filter-btn"
                                style={{ width: "auto" }}
                                onClick={handleDownloadData}
                            >
                                <img
                                    src={downloadIcon}
                                    alt=""
                                    className="mr-1"
                                />{" "}
                                Download Data
                            </Button>
                        </HoverAuthorizeTooltip>
                    </Col>
                </Row>
                {dateFilterMessage ? (
                    <Row>
                        <Col>
                            <p className="text-danger mt-1 mb-0" role="alert">
                                {dateFilterMessage}
                            </p>
                        </Col>
                    </Row>
                ) : null}
            </div>
            {isFilterData && filterStart && filterEnd && (
                <Row className="mb-2">
                    <Col className="d-flex align-items-center">
                        <Typography variant="body1">Filtered Data :</Typography>
                        <Chip
                            className="ml-2"
                            sx={{
                                backgroundColor: "#00cbaf",
                                color: "black",
                            }}
                            size="medium"
                            label={`Start Date: ${dateTimeFormatter.date(
                                filterStart
                            )} - ${dateTimeFormatter.time(
                                filterStart
                            )} - End Date: ${dateTimeFormatter.date(
                                filterEnd
                            )} - ${dateTimeFormatter.time(filterEnd)}`}
                            variant="outlined"
                            onDelete={handleClearFilter}
                        />
                    </Col>
                </Row>
            )}
            <Row>
                <Col>
                    <HistoryTable journals={allJournals} />
                </Col>
            </Row>
            {!isFilterData && (
                <Row className="mt-3">
                    <Col className="text-center">
                        <Button onClick={handleLoadMore} disabled={isLoading}>
                            {isLoading ? "Loading..." : "Load More"}
                        </Button>
                    </Col>
                </Row>
            )}
        </>
    );
};
